'use client';

import { useEffect, useState } from 'react';
import { VersionManager } from '@/lib/version-manager';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * 简化的版本检查组件
 * 定期检查更新，提供友好的更新提示
 */
export function SimpleVersionChecker() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const checkForUpdates = async () => {
      const hasUpdate = await VersionManager.checkForUpdates();
      if (hasUpdate) {
        setShowUpdatePrompt(true);
        // 延迟显示动画
        setTimeout(() => setIsVisible(true), 10);
      }
    };
    
    // 初始检查
    checkForUpdates();
    
    // 定期检查（1小时）
    const checkInterval = setInterval(checkForUpdates, 3600000);
    
    // 页面获得焦点时检查（限制频率）
    const handleFocus = () => {
      checkForUpdates();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(checkInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
  
  const handleUpdate = async () => {
    setIsUpdating(true);
    await VersionManager.applyUpdate();
  };
  
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => setShowUpdatePrompt(false), 200);
    // 30分钟后再次提醒
    setTimeout(() => {
      VersionManager.resetCheckTimer();
    }, 1800000);
  };
  
  if (!showUpdatePrompt) return null;
  
  return (
    <div
      className={cn(
        "fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50",
        "transition-all duration-200 ease-out",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
      )}
    >
      <div className="bg-white rounded-lg shadow-lg p-4 border border-gray-200">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-sm font-semibold">🎉 发现新版本</h3>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          应用有更新，建议刷新以获得最新功能和修复。
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="flex-1 bg-blue-600 text-white text-sm rounded-lg py-2 px-4 
                     hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            {isUpdating ? '更新中...' : '立即更新'}
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 border border-gray-300 text-gray-700 text-sm rounded-lg py-2 px-4
                     hover:bg-gray-50 transition-colors"
          >
            稍后提醒
          </button>
        </div>
      </div>
    </div>
  );
}
